"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

interface ShortLinkContextValue {
  shortCode?: string;
  southpawJobId?: string;
  attribution: Record<string, string>;
}

const ShortLinkContext = createContext<ShortLinkContextValue | undefined>(
  undefined
);

export const useShortLink = () => useContext(ShortLinkContext);

function readFromStorage(): ShortLinkContextValue {
  if (typeof window === "undefined") return { attribution: {} };
  const shortCode = sessionStorage.getItem("short_code") || undefined;
  const southpawJobId = sessionStorage.getItem("job_id") || undefined;
  const attributionRaw = sessionStorage.getItem("short_link_attr");
  let attribution: Record<string, string> = {};
  if (attributionRaw) {
    try {
      attribution = JSON.parse(attributionRaw);
    } catch {
      attribution = {};
    }
  }
  return { shortCode, southpawJobId, attribution };
}

function persistToStorage(value: ShortLinkContextValue) {
  if (typeof window === "undefined") return;
  if (value.shortCode) {
    sessionStorage.setItem("short_code", value.shortCode);
  }
  if (value.southpawJobId) {
    sessionStorage.setItem("job_id", value.southpawJobId);
  }
  sessionStorage.setItem(
    "short_link_attr",
    JSON.stringify(value.attribution || {})
  );
}

function clearShortLinkStorage() {
  sessionStorage.removeItem("short_code");
  sessionStorage.removeItem("job_id");
  sessionStorage.removeItem("short_link_attr");
}

const CLEAR_SHORT_LINK_PATHS = [
  "/",
  "/feed",
  "/nearby",
  "/profile",
  "/notifications",
  "/merch",
  "/search",
  "/library",
  "/about",
  "/tos",
  "/privacy",
  "/tixologi",
];

// This file and logic should be deleted long term as it's not reliable (hard to map all possible side effects) when doing tracking this way
// Suitable short term for experimentaing with unified messaging analytics
export function ShortLinkProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [state, setState] = useState<ShortLinkContextValue>(readFromStorage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    // Check if there are new short link params or southpaw job id in the URL
    const urlShortCode = params.get("sc");
    const urlSouthpawJobId = params.get("sid");
    const hasNewShortLinkParams = urlShortCode || urlSouthpawJobId;

    // Smart clearing: clear if on clear path AND no new short link params
    const shouldClear =
      CLEAR_SHORT_LINK_PATHS.some((path) => {
        // Special case: "/" should only match exactly "/", not all paths starting with "/"
        if (path === "/") {
          return pathname === "/";
        }
        // All other paths use startsWith for subdirectories
        return pathname.startsWith(path);
      }) && !hasNewShortLinkParams;

    if (shouldClear) {
      clearShortLinkStorage();
      setState({ attribution: {} });
      return;
    }

    // Process URL parameters (preserve existing data if no new params)
    const shortCode = urlShortCode || state.shortCode;
    const southpawJobId = urlSouthpawJobId || state.southpawJobId;
    const attribution: Record<string, string> = { ...state.attribution };

    params.forEach((value, key) => {
      attribution[key] = value;
    });

    const nextState: ShortLinkContextValue = {
      shortCode: shortCode ?? undefined,
      southpawJobId: southpawJobId ?? undefined,
      attribution,
    };

    setState(nextState);
    persistToStorage(nextState);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams.toString()]);

  return (
    <ShortLinkContext.Provider value={state}>
      {children}
    </ShortLinkContext.Provider>
  );
}

// Utility for non-React code (e.g. analytics wrapper)
export function getStoredShortLinkContext(): ShortLinkContextValue | null {
  if (typeof window === "undefined") {
    return null;
  }
  const shortCode = sessionStorage.getItem("short_code") || undefined;
  const southpawJobId = sessionStorage.getItem("job_id") || undefined;
  const attributionRaw = sessionStorage.getItem("short_link_attr");
  let attribution: Record<string, string> = {};
  if (attributionRaw) {
    try {
      attribution = JSON.parse(attributionRaw);
    } catch {
      attribution = {};
    }
  }
  if (!shortCode && !southpawJobId) return null;
  return { shortCode, southpawJobId, attribution };
}
